<template>
  <section class="search-filter">
    <div class="search-filter__row search-filter__row--categories">
      <div class="search-filter__label-col">
        <div class="search-filter__label-line">
          <h3 class="search-filter__label">商标大类</h3>
          <button
            type="button"
            :class="[
              'search-filter__text-link',
              { 'is-active': !selectedCategoryIds.length }
            ]"
            @click="selectCategory('')"
          >不限</button>
        </div>
        <button
          type="button"
          :class="[
            'search-filter__multi-btn',
            { 'is-active': filters.sameNameMulti }
          ]"
          @click="toggleSameNameMulti"
        >同名多类</button>
      </div>
      <div class="search-filter__class-wrap">
        <div class="search-filter__class-grid">
          <button
            v-for="cat in categories"
            :key="cat.id"
            type="button"
            :class="[
              'search-filter__class-item',
              { 'is-active': isCategoryActive(cat.id) }
            ]"
            @click="selectCategory(String(cat.id))"
          >
            <span class="search-filter__class-id">{{ formatId(cat.id) }}类</span>
            {{ cat.name }}
          </button>
        </div>

        <div
          v-if="groupSections.length"
          class="search-filter__groups-panel"
        >
          <div
            v-for="section in groupSections"
            :key="section.categoryId"
            class="search-filter__group-row"
          >
            <h4 class="search-filter__group-label">{{ section.label }}</h4>
            <div class="search-filter__group-codes">
              <button
                v-for="group in section.groups"
                :key="group.code"
                type="button"
                :class="[
                  'search-filter__group-code',
                  { 'is-active': isGroupActive(group.code) }
                ]"
                @click="toggleGroup(group.code)"
              >{{ group.code }}</button>
            </div>
          </div>
          <p class="search-filter__group-count">
            已选择{{ selectedGroupCount }}项
          </p>
        </div>
      </div>
    </div>

    <div class="search-filter__row search-filter__row--tags">
      <div class="search-filter__label-col">
        <div class="search-filter__label-line">
          <h3 class="search-filter__label">筛选条件</h3>
          <button
            type="button"
            class="search-filter__text-link search-filter__text-link--action"
            @click="$emit('clear')"
          >清空</button>
        </div>
      </div>
      <div class="search-filter__tags">
        <template v-if="activeTags.length">
          <span
            v-for="tag in activeTags"
            :key="tag.key"
            class="search-filter__tag"
          >
            {{ tag.label }}
            <button
              type="button"
              class="search-filter__tag-remove"
              aria-label="移除筛选条件"
              @click="removeTag(tag.key)"
            >×</button>
          </span>
        </template>
        <span v-else class="search-filter__empty">暂无筛选条件</span>
      </div>
    </div>
  </section>
</template>

<script>
import { fetchSubClassify } from '@/api/classify'
import { ALL_CATEGORIES, formatCategoryId } from '@/constants/allCategories'
import {
  SEARCH_FILTER_OPTIONS,
  parseCategoryFilter,
  parseGroupFilter,
  formatGroupFilter,
  filterGroupsByCategories,
  patchCategorySelection
} from '@/constants/searchPage'

export default {
  name: 'SearchFilterPanel',
  props: {
    filters: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      categories: ALL_CATEGORIES,
      filterOptions: SEARCH_FILTER_OPTIONS,
      subClassify: []
    }
  },
  computed: {
    selectedCategoryIds() {
      return parseCategoryFilter(this.filters.category)
    },
    selectedGroupCodes() {
      return parseGroupFilter(this.filters.group)
    },
    selectedGroupCount() {
      return this.selectedGroupCodes.length
    },
    groupSections() {
      return this.selectedCategoryIds.map(id => {
        const catId = Number(id)
        const sub = this.subClassify.find(
          item => Number(item.xh) === catId
        )
        const groups = sub && sub.list ? sub.list : []
        return {
          categoryId: id,
          label: `${formatCategoryId(catId)}-类似群组`,
          groups
        }
      })
    },
    activeTags() {
      const tags = []
      const filters = this.filters

      if (filters.keyword) {
        tags.push({
          key: 'keyword',
          label: `关键词：${filters.keyword}`
        })
      }

      if (filters.sameNameMulti) {
        tags.push({
          key: 'sameNameMulti',
          label: '同名多类'
        })
      }

      this.selectedCategoryIds.forEach(id => {
        const cat = ALL_CATEGORIES.find(item => String(item.id) === id)
        if (cat) {
          tags.push({
            key: `category-${id}`,
            label: `${formatCategoryId(cat.id)}类 ${cat.name}`
          })
        }
      })

      this.selectedGroupCodes.forEach(code => {
        tags.push({
          key: `group-${code}`,
          label: `群组 ${code}`
        })
      })

      if (filters.price) {
        const price = this.filterOptions.prices.find(item => item.value === filters.price)
        if (price) {
          tags.push({ key: 'price', label: price.label })
        }
      } else if (filters.priceMin || filters.priceMax) {
        const min = filters.priceMin || '0'
        const max = filters.priceMax || '不限'
        tags.push({
          key: 'priceCustom',
          label: `价格：${min}-${max}`
        })
      }

      if (filters.status && filters.status !== 'on_sale') {
        const status = this.filterOptions.statuses.find(item => item.value === filters.status)
        if (status) {
          tags.push({ key: 'status', label: status.label })
        }
      }

      if (filters.sort && filters.sort !== 'default') {
        const sort = this.filterOptions.sorts.find(item => item.value === filters.sort)
        if (sort) {
          tags.push({ key: 'sort', label: sort.label })
        }
      }

      return tags
    }
  },
  mounted() {
    this.loadSubClassify()
  },
  methods: {
    formatId: formatCategoryId,
    loadSubClassify() {
      fetchSubClassify()
        .then(list => {
          this.subClassify = list
        })
        .catch(() => {
          this.subClassify = []
        })
    },
    buildGroupPatch(nextCategoryIds) {
      return formatGroupFilter(
        filterGroupsByCategories(this.selectedGroupCodes, nextCategoryIds)
      )
    },
    isCategoryActive(categoryId) {
      return this.selectedCategoryIds.includes(String(categoryId))
    },
    isGroupActive(code) {
      return this.selectedGroupCodes.includes(String(code))
    },
    selectCategory(categoryId) {
      const nextCategory = patchCategorySelection(
        this.filters.category,
        categoryId,
        this.filters.sameNameMulti
      )
      const nextCategoryIds = parseCategoryFilter(nextCategory)

      this.$emit('change', {
        category: nextCategory,
        group: categoryId ? this.buildGroupPatch(nextCategoryIds) : ''
      })
    },
    toggleGroup(code) {
      const codes = [...this.selectedGroupCodes]
      const value = String(code)
      const index = codes.indexOf(value)

      if (index >= 0) {
        codes.splice(index, 1)
      } else {
        codes.push(value)
      }

      this.$emit('change', {
        group: formatGroupFilter(codes)
      })
    },
    toggleSameNameMulti() {
      const next = !this.filters.sameNameMulti
      const patch = { sameNameMulti: next }

      if (!next && this.selectedCategoryIds.length > 1) {
        const nextCategory = this.selectedCategoryIds[0]
        patch.category = nextCategory
        patch.group = this.buildGroupPatch([nextCategory])
      }

      this.$emit('change', patch)
    },
    removeTag(key) {
      const patch = {}
      if (key === 'keyword') patch.keyword = ''
      if (key === 'sameNameMulti') patch.sameNameMulti = false
      if (key.startsWith('category-')) {
        const id = key.replace('category-', '')
        const ids = this.selectedCategoryIds.filter(item => item !== id)
        patch.category = ids.join(',')
        patch.group = this.buildGroupPatch(ids)
      }
      if (key.startsWith('group-')) {
        const code = key.replace('group-', '')
        patch.group = formatGroupFilter(
          this.selectedGroupCodes.filter(item => item !== code)
        )
      }
      if (key === 'status') patch.status = ''
      if (key === 'sort') {
        patch.sort = 'default'
        patch.activeSort = 'default'
      }
      if (key === 'price' || key === 'priceCustom') {
        patch.price = ''
        patch.priceMin = ''
        patch.priceMax = ''
      }
      this.$emit('change', patch)
    }
  }
}
</script>

<style scoped>
.search-filter {
  background: var(--color-bg-page);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-panel);
  padding: var(--search-panel-padding-y) var(--search-panel-padding-x);
}

.search-filter__row {
  display: flex;
  gap: var(--search-filter-row-gap);
}

.search-filter__row + .search-filter__row {
  margin-top: var(--search-filter-row-gap);
  padding-top: var(--search-filter-row-gap);
  border-top: 1px dashed var(--color-border-light);
}

.search-filter__label-col {
  flex-shrink: 0;
  width: var(--search-filter-label-width);
  min-width: var(--search-filter-label-width);
}

.search-filter__label-line {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--space-sm);
  min-height: var(--search-filter-line-height);
  white-space: nowrap;
}

.search-filter__label {
  flex-shrink: 0;
  font-size: var(--search-filter-font-size);
  font-weight: 600;
  color: var(--color-text-primary);
  line-height: var(--search-filter-line-height);
  white-space: nowrap;
}

.search-filter__text-link {
  flex-shrink: 0;
  border: none;
  background: transparent;
  padding: 0;
  font-size: var(--search-filter-font-size);
  font-weight: 400;
  color: var(--color-text-primary);
  line-height: var(--search-filter-line-height);
  white-space: nowrap;
  cursor: pointer;
}

.search-filter__text-link:hover,
.search-filter__text-link.is-active {
  color: var(--color-primary);
  font-weight: 600;
}

.search-filter__text-link--action {
  color: var(--color-primary);
}

.search-filter__text-link--action:hover {
  color: var(--color-primary-dark);
}

.search-filter__multi-btn {
  display: inline-block;
  margin-top: 2px;
  padding: 2px var(--space-sm);
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-border-light);
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  line-height: 1.4;
  white-space: nowrap;
  cursor: pointer;
  max-width: 100%;
}

.search-filter__multi-btn.is-active {
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-weight: 600;
}

.search-filter__class-wrap {
  flex: 1;
  min-width: 0;
}

.search-filter__class-grid {
  display: grid;
  grid-template-columns: repeat(var(--search-filter-class-columns), minmax(0, 1fr));
  gap: var(--search-filter-class-row-gap) var(--search-filter-class-col-gap);
}

.search-filter__class-item {
  border: none;
  background: transparent;
  text-align: left;
  font-size: var(--search-filter-font-size);
  color: var(--color-text-primary);
  line-height: var(--search-filter-line-height);
  padding: 0;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-filter__class-item:hover,
.search-filter__class-item.is-active {
  color: var(--color-primary);
  font-weight: 600;
}

.search-filter__class-id {
  margin-right: var(--space-xs);
}

.search-filter__groups-panel {
  margin-top: var(--search-filter-row-gap);
  padding: var(--search-filter-groups-padding);
  background: var(--search-filter-groups-bg);
  border-radius: var(--radius-sm);
}

.search-filter__group-row {
  display: flex;
  gap: var(--search-filter-row-gap);
}

.search-filter__group-row + .search-filter__group-row {
  margin-top: var(--search-filter-group-row-gap);
}

.search-filter__group-label {
  flex-shrink: 0;
  width: var(--search-filter-group-label-width);
  font-size: var(--search-filter-font-size);
  font-weight: 600;
  color: var(--color-text-primary);
  line-height: var(--search-filter-line-height);
}

.search-filter__group-codes {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--search-filter-group-code-gap);
}

.search-filter__group-code {
  border: none;
  background: transparent;
  font-size: var(--search-filter-font-size);
  color: var(--color-text-primary);
  line-height: var(--search-filter-line-height);
  padding: 0;
  cursor: pointer;
}

.search-filter__group-code:hover,
.search-filter__group-code.is-active {
  color: var(--color-primary);
  font-weight: 600;
}

.search-filter__group-count {
  margin-top: var(--search-filter-row-gap);
  text-align: right;
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
  line-height: 1.4;
}

.search-filter__tags {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-xs) var(--space-sm);
  min-height: var(--search-filter-line-height);
}

.search-filter__tag {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  height: 24px;
  padding: 0 var(--space-sm);
  border: 1px solid var(--color-primary);
  border-radius: var(--radius-sm);
  background: var(--color-bg-page);
  color: var(--color-primary);
  font-size: var(--font-size-sm);
}

.search-filter__tag-remove {
  border: none;
  background: transparent;
  color: inherit;
  font-size: var(--font-size-lg);
  line-height: 1;
  cursor: pointer;
}

.search-filter__empty {
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
}
</style>
